import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AddMcpServer, RemoveMcpServer } from "../../../schemas/codegen";
import { chatWebSocketManager } from "../websocket/ChatWebSocketManager";
import { QUERY_KEYS } from "./queryKeys";

export interface AddMcpServerParams {
  chatId: string;
  url: string;
  name: string;
}

export interface RemoveMcpServerParams {
  chatId: string;
  serverId: string;
}

export function useAddMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      chatId,
      url,
      name,
    }: AddMcpServerParams): Promise<boolean> => {
      const connection = chatWebSocketManager.getConnection(chatId);

      const addCommand: AddMcpServer = {
        type: "mcp.server.add",
        url,
        name,
      };

      // Ensure connection is established
      if (!connection.isConnected()) {
        try {
          await connection.connect();
        } catch (error) {
          console.error(
            `Failed to connect to chat ${chatId} for MCP server addition:`,
            error,
          );
          throw new Error("Failed to connect to chat");
        }
      }

      // Send add command via websocket
      const success = connection.send(addCommand);
      if (!success) {
        throw new Error("Failed to send add MCP server command");
      }
      return success;
    },
    onSuccess: (_, { chatId }) => {
      // Invalidate and refetch the MCP servers query
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MCP_SERVERS, chatId],
      });
    },
  });
}

export function useRemoveMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      chatId,
      serverId,
    }: RemoveMcpServerParams): Promise<boolean> => {
      const connection = chatWebSocketManager.getConnection(chatId);

      const removeCommand: RemoveMcpServer = {
        type: "mcp.server.remove",
        serverId,
      };

      // Ensure connection is established
      if (!connection.isConnected()) {
        try {
          await connection.connect();
        } catch (error) {
          console.error(
            `Failed to connect to chat ${chatId} for MCP server removal:`,
            error,
          );
          throw new Error("Failed to connect to chat");
        }
      }

      // Send remove command via websocket
      const success = connection.send(removeCommand);
      if (!success) {
        throw new Error("Failed to send remove MCP server command");
      }
      return success;
    },
    onSuccess: (_, { chatId }) => {
      // Invalidate and refetch the MCP servers query
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MCP_SERVERS, chatId],
      });
    },
  });
}
